import { DisabledFeatureModalable } from '../../models/disabled-feature-modalable';
import { TextSelection } from '../../models/text-selection';
import { EditMoveableBookFeatureEvent } from '../../utils/events/edit-moveable-book-feature-event';
import { Rect } from '../../models/rect';
import { PageDrawing } from '../../models/page-drawing';
import { ApplicationStateService } from '../../services/application-state.service';
import { BookFeatureActivationService } from '../../services/book-feature-activation.service';
import { FabricService } from '../../services/fabric.service';
import { ModalService } from '../../services/modal.service';
import { CommunicationService } from '../../services/communication.service';
import { RectPageEditMode } from '../../utils/page-edit-modes/rect-page-edit-mode';
import { SelectionPageEditMode } from '../../utils/page-edit-modes/selection-page-edit-mode';
import { DrawEditMode } from '../../utils/page-edit-modes/draw-edit-mode';
import { TextCopy } from '../../models/text-copy';
import { CopyingPageEditMode } from '../../utils/page-edit-modes/copying-page-edit-mode';
import {
    OfflineModeModalComponent
} from '../bookviewer/pages/modal-container/offline-mode-modal/offline-mode-modal.component';

export class AbstractToolsBarComponent {

    applicationStateService: ApplicationStateService;
    featureActivationService: BookFeatureActivationService;
    protected fabricService: FabricService;
    protected modalService: ModalService;
    protected communicationService: CommunicationService;

    toggleTextHighlighting($event): void {

        this.checkServices();

        if (this.applicationStateService.isOfflineModeEnabled) {
            this.modalService.openModal(OfflineModeModalComponent);

            return;
        }

        if (!this.featureActivationService.textSelectionActivated ||
            !this.applicationStateService.bookConfig.bookPermissions.textSelection.create
        ) {

            const disabledFeatureModal = new DisabledFeatureModalable();
            disabledFeatureModal.type = ModalService.HIGHLIGHT_TEXT;
            disabledFeatureModal.iconUrl = 'draw-mode/icon-tool-marker.png';
            this.modalService.openModal(disabledFeatureModal);

            return;
        }

        this.fabricService.setCurrentDrawAction(undefined);
        if (this.applicationStateService.isPageEditable &&
            this.inSelectionPageEditMode()) {
            this.unsetPageEditMode();

            return;
        }

        this.activateNoteVisibility();
        this.createAndSendEvent(new TextSelection());
        this.showTooltip($event);

        // Force hiding tooltip because the event never makes it to bootstrap
        // @ts-ignore
        bootstrap.Dropdown.getOrCreateInstance('#drawing-tools').hide();
        ($($event.target).closest('[data-bs-toggle="tooltip"]') as any).tooltip('hide');
    }

    toggleAreaHighlighting($event): void {

        this.checkServices();

        if (this.applicationStateService.isOfflineModeEnabled) {
            this.modalService.openModal(OfflineModeModalComponent);

            return;
        }

        if (!this.featureActivationService.rectsActivated ||
            !this.applicationStateService.bookConfig.bookPermissions.rect.create) {

            const disabledFeatureModal = new DisabledFeatureModalable();
            disabledFeatureModal.type = ModalService.CREATE_NOTE;
            disabledFeatureModal.iconUrl = 'draw-mode/icon-tool-notes.png';
            this.modalService.openModal(disabledFeatureModal);

            return;
        }

        this.fabricService.setCurrentDrawAction(undefined);
        if (this.applicationStateService.isPageEditable &&
            this.inRectPageEditMode()) {
            this.unsetPageEditMode();

            return;
        }

        this.activateNoteVisibility();
        this.createAndSendEvent(new Rect());
        this.showTooltip($event);

        // Force hiding tooltip because the event never makes it to bootstrap
        // @ts-ignore
        bootstrap.Dropdown.getOrCreateInstance('#drawing-tools').hide();
        ($($event.target).closest('[data-bs-toggle="tooltip"]') as any).tooltip('hide');

    }

    toggleDrawing($event): void {

        this.checkServices();

        if (this.applicationStateService.isOfflineModeEnabled) {
            this.modalService.openModal(OfflineModeModalComponent);

            return;
        }

        if ((!this.featureActivationService.pageDrawingActivated ||
            !this.applicationStateService.bookConfig.bookPermissions.pageDrawing.create)) {
            this.modalService.openDisabledDrawingModeModal();

            return;
        }

        if (this.applicationStateService.isPageEditable &&
            this.inDrawEditMode()) {
            this.unsetPageEditMode();
            this.fabricService.setCurrentDrawAction(undefined);

            return;
        }

        this.activatePageDrawingVisibility();
        const pageDrawing = new PageDrawing();
        this.createAndSendEvent(pageDrawing);
        ($('.dropdown-tooltip') as any).tooltip('hide');
    }

    toggleTextCopying($event): void {

        this.checkServices();

        if (this.applicationStateService.isOfflineModeEnabled) {
            this.modalService.openModal(OfflineModeModalComponent);

            return;
        }

        if (!this.featureActivationService.textSelectionActivated ||
            !this.applicationStateService.bookConfig.bookPermissions.pageTexts.use
        ) {

            const disabledFeatureModal = new DisabledFeatureModalable();
            disabledFeatureModal.type = ModalService.COPY_TEXT;
            disabledFeatureModal.iconUrl = 'draw-mode/icon-tool-copy.png';
            this.modalService.openModal(disabledFeatureModal);

            return;
        }

        this.fabricService.setCurrentDrawAction(undefined);
        if (this.applicationStateService.isPageEditable &&
            this.inCopyingPageEditMode()) {
            this.unsetPageEditMode();

            return;
        }

        this.activateNoteVisibility();
        this.createAndSendEvent(new TextCopy());
        this.showTooltip($event);

        // Force hiding tooltip because the event never makes it to bootstrap
        // @ts-ignore
        bootstrap.Dropdown.getOrCreateInstance('#drawing-tools').hide();
        ($($event.target).closest('[data-bs-toggle="tooltip"]') as any).tooltip('hide');
    }

    /**
     * Check if we are in the rect mode.
     *
     * @returns {boolean}
     */
    inRectPageEditMode(): boolean {
        return (this.applicationStateService.pageEditMode instanceof RectPageEditMode);
    }

    /**
     * Check if we are in the text selection mode.
     *
     * @returns {boolean}
     */
    inSelectionPageEditMode(): boolean {
        return (this.applicationStateService.pageEditMode instanceof SelectionPageEditMode);
    }

    /**
     * Check if we are in the text selection mode.
     *
     * @returns {boolean}
     */
    inCopyingPageEditMode(): boolean {
        return (this.applicationStateService.pageEditMode instanceof CopyingPageEditMode);
    }

    /**
     * Checks if the DrawingMode is active
     * @returns {boolean}
     */
    inDrawEditMode(): boolean {
        return this.applicationStateService.pageEditMode && this.applicationStateService.pageEditMode instanceof DrawEditMode;
    }

    private activateNoteVisibility(): void {

        if (!this.featureActivationService.textSelectionsVisible) {
            this.featureActivationService.textSelectionsVisible = true;
            this.featureActivationService.rectsVisible = true;
            this.communicationService.announceHighlightTextboxes();
        }
    }

    private activatePageDrawingVisibility(): void {
        if (!this.featureActivationService.pageDrawingsVisible) {
            this.featureActivationService.pageDrawingsVisible = true;
        }
    }

    private unsetPageEditMode(): void {
        if (this.applicationStateService.pageEditMode && this.applicationStateService.pageEditMode.type === 'COPY') {
            // @ts-ignore
            this.applicationStateService.pageEditMode.unsetSelection();
        }
        this.applicationStateService.isPageEditable = false;
        this.applicationStateService.pageEditMode = undefined;
        this.communicationService.announceMoveableBookFeatureEdit(new EditMoveableBookFeatureEvent());
    }

    private createAndSendEvent(element): void {
        this.applicationStateService.isPageEditable = true;
        const event = new EditMoveableBookFeatureEvent();
        event.moveableBookFeature = element;
        this.communicationService.announceMoveableBookFeatureEdit(event);
    }

    private showTooltip($event): void {
        // Force showing tooltip because the event never makes it to bootstrap
        ($($event.target) as any).closest('[data-bs-toggle="tooltip"]')
            .tooltip('show');
    }

    private checkServices(): void {
        if (!this.applicationStateService ||
            !this.featureActivationService ||
            !this.fabricService ||
            !this.modalService ||
            !this.communicationService) {
            throw new Error('Services are not provided');
        }
    }

    onEscToggleButton(): void {
        this.unsetPageEditMode();
    }
}
